// Recent trouble for feedback reports (ADR-0050): the last 20 failed API requests (method, path without its query, status, problem code and W3C trace id) and the last 20 script errors and unhandled rejections (message and where), kept in memory only, plus the page, build, browser, window size and language a report sends with them.
import type { FeedbackContext, FeedbackErrorSeen, FeedbackProblemSeen } from "@/api/__generated__";
import { entryScript } from "@/app/entry-script";

export const MAX_RECENT = 20;
const MAX_MESSAGE = 1000;
const MAX_SOURCE = 500;
const MAX_PATH = 500;
const MAX_PAGE = 2000;
const MAX_BROWSER = 500;

const problems: FeedbackProblemSeen[] = [];
const errors: FeedbackErrorSeen[] = [];

function clip(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

function remember<T>(list: T[], item: T): void {
  list.push(item);
  if (list.length > MAX_RECENT) {
    list.splice(0, list.length - MAX_RECENT);
  }
}

export function traceIdOf(traceparent: string | null): string | null {
  const parts = traceparent?.split("-") ?? [];
  return parts.length === 4 && /^[0-9a-f]{32}$/u.test(parts[1] ?? "") ? (parts[1] ?? null) : null;
}

function pathOf(url: string): string {
  try {
    return clip(new URL(url, window.location.origin).pathname, MAX_PATH);
  } catch {
    return clip(url.split("?")[0] ?? url, MAX_PATH);
  }
}

export function recordProblem(problem: {
  method: string;
  url: string;
  status: number;
  code: string;
  traceId: string | null;
}): void {
  remember(problems, {
    at: new Date().toISOString(),
    method: clip(problem.method.toUpperCase(), 16),
    path: pathOf(problem.url),
    status: problem.status,
    code: clip(problem.code, 100),
    trace_id: problem.traceId,
  });
}

function reasonText(reason: unknown): string {
  if (reason instanceof Error) {
    return `${reason.name}: ${reason.message}`;
  }
  return typeof reason === "string" ? reason : "Unhandled promise rejection";
}

export function recordError(message: string, source: string | null): void {
  remember(errors, {
    at: new Date().toISOString(),
    message: clip(message, MAX_MESSAGE),
    source: source === null ? null : clip(source, MAX_SOURCE),
  });
}

export function installErrorCapture(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    const source = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : null;
    recordError(event.message || reasonText(event.error), source);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    recordError(reasonText(event.reason), null);
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

export function webRelease(): string {
  const entry = typeof document === "undefined" ? null : entryScript(document);
  const name = entry?.split("/").at(-1);
  return name === undefined ? __APP_VERSION__ : `${__APP_VERSION__} (${name})`;
}

export function feedbackContext(): FeedbackContext {
  return {
    page: clip(`${window.location.pathname}${window.location.search}`, MAX_PAGE),
    web_release: webRelease(),
    browser: clip(navigator.userAgent, MAX_BROWSER),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language || null,
    problems: [...problems],
    errors: [...errors],
  };
}

export function clearDiagnostics(): void {
  problems.length = 0;
  errors.length = 0;
}
